import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { supabase } from '../config/supabase'
import ReCAPTCHA from "react-google-recaptcha"

const Register = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [phone, setPhone] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { signUp } = useAuth()
  const navigate = useNavigate()
  const [captchaToken, setCaptchaToken] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      setError('')

      // ตรวจสอบอ่าผ่าน captcha หรือไม่
      if (!captchaToken) {
        throw new Error('กรุณายืนยันตัวตนด้วย reCAPTCHA')
      }

      // ตรวจสอบอ่าว่าง
      if (!email.trim() || !phone.trim() || !displayName.trim()) {
        throw new Error('กรุณากรอกข้อมูลให้ครบถ้วน')
      }

      // ตรวจสอบรูปแบบเบอร์โทร
      if (!/^\d{10}$/.test(phone)) {
        throw new Error('กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง (10 หลัก)')
      }

      // ตรวจสอบอีเมลซ้ำ
      const { data: emailCheck, error: emailError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .not('email', 'is', null)
        .single()

      if (emailCheck) {
        throw new Error('อีเมลนี้ถูกใช้งานแล้ว')
      }

      // ตรวจสอบเบอร์โทรซ้ำ
      const { data: phoneCheck, error: phoneError } = await supabase
        .from('profiles')
        .select('id')
        .eq('phone', phone)
        .not('phone', 'is', null)
        .single()

      if (phoneCheck) {
        throw new Error('เบอร์โทรศัพท์นี้ถูกใช้งานแล้ว')
      }

      // 1. สมัครสมาชิกด้วย email/password
      const { data: authData, error: authError } = await signUp(email, password)
      if (authError) throw authError

      // ตรวจสอบว่ามี user id
      if (!authData?.user?.id) {
        throw new Error('ไม่สามารถสร้างบัญชีผู้ใช้ได้')
      }

      // ไม่ต้อง update profiles แล้วเพราะจะถูกสร้างโดย trigger อัตโนมัติ
      navigate('/dashboard')
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Helmet>
        <title>สมัครสมาชิก</title>
      </Helmet>
      <div className="max-w-md w-full bg-gray-900 p-8 rounded-lg shadow-xl">
        <h2 className="text-2xl font-bold mb-5 text-white">สมัครสมาชิก</h2>
        {error && <div className="bg-red-900/50 text-red-200 p-3 mb-4 rounded border border-red-500">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300">อีเมล</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300">ชื่อ Discord หรือชื่อที่แสดง</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300">เบอร์โทรศัพท์</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              pattern="[0-9]{10}"
              title="กรุณากรอกเบอร์โทรศัพท์ 10 หลัก"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300">รหัสผ่าน</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              minLength={6}
            />
          </div>
          <div className="mb-4 flex justify-center">
            <ReCAPTCHA
              sitekey="6Lecqo4qAAAAAOMQINIjV8NgNud5GAtw-XKxRlcw"
              onChange={(token) => setCaptchaToken(token)}
              theme="dark"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !captchaToken}
            className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700 transition-colors mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'กำลังสมัครสมาชิก...' : 'สมัครสมาชิก'}
          </button>
        </form>
        
        <div className="text-center">
          <p className="text-gray-400 mb-2">มีบัญชีอยู่แล้ว?</p>
          <Link 
            to="/login" 
            className="inline-block bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700 transition-colors"
          >
            เข้าสู่ระบบ
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Register